import React from 'react';
import { FormSection, FormSectionImperativeActions } from '../../../common';
import { FormOnSubmit } from '../../../common/FormSection/FormSection.types';
import { FormInputs, FormInputsHandle } from './FormInputs/FormInputs';
import { LoginCallback, PromptAuthPageCallback } from '../types';
import { ResetPasswordLink } from './FormInputs/ResetPasswordLink';
import { classes } from './FormSection.st.css';
import { ForcedState } from '../../../../constants';
import { ICaptchaSettingsOption } from '../../../types';
import { serverErrorsHandler } from '../../../utils/errorHandler';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { useCaptchaWithFallback } from '../../../Captcha/providers/useCaptchaWithFallback';

interface LoginFormSectionProps {
  login: LoginCallback;
  setError: (error?: string) => void;
  isEditor: boolean;
  isPreview?: boolean;
  forceView?: ForcedState;
  recaptchaOption: ICaptchaSettingsOption;
  promptAuthPage: PromptAuthPageCallback;
  autoFocus?: boolean;
  allowPostAuthRedirect?: boolean;
}

export const LoginFormSection = ({
  login,
  setError,
  isEditor,
  isPreview,
  forceView,
  recaptchaOption,
  promptAuthPage,
  autoFocus = false,
  allowPostAuthRedirect = false,
}: LoginFormSectionProps) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);

  // Ref to access FormInputs validation
  const formInputsRef = React.useRef<FormInputsHandle>(null);
  // Ref to access FormSection imperative actions
  const formSectionRef = React.useRef<FormSectionImperativeActions>(null);

  const { shouldShowVisibleCaptcha, tryGetInvisibleToken, handleCaptchaError } =
    useCaptchaWithFallback({
      recaptchaOption,
      isEditor,
      language: i18n.language,
      action: 'login',
    });

  /**
   * Custom validation function that FormSection can call
   */
  const validateFormFields = () => {
    return formInputsRef.current?.validate() ?? { isValid: false };
  };

  /**
   * Handle form submission - only called if validation passes
   */
  const handleLogin: FormOnSubmit = async ({ recaptchaToken }) => {
    setIsLoading(true);
    setError(); // Clear any previous errors

    try {
      // If visible captcha is being used, skip invisible captcha
      let invisibleRecaptchaToken: string | undefined;
      if (!shouldShowVisibleCaptcha) {
        const result = await tryGetInvisibleToken();

        if (result.status === 'VISIBLE_CAPTCHA_REQUIRED') {
          // Fallback triggered, visible captcha will appear on next render
          return;
        }
        invisibleRecaptchaToken = result.token;
      }

      await login(email, password, {
        recaptchaToken,
        invisibleRecaptchaToken,
      });
    } catch (err) {
      const { errorKey, errorCode } = serverErrorsHandler(err as any);
      handleCaptchaError(errorCode);
      const errorMessage = t(errorKey, {
        defaultValue: t('membersErrorGeneral'),
      });
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      formSectionRef.current?.resetCaptcha();
    }
  };

  return (
    <FormSection
      formType="login"
      onSubmit={handleLogin}
      isLoading={isLoading}
      showCaptcha={shouldShowVisibleCaptcha}
      isEditor={isEditor}
      isPreview={isPreview}
      forceView={forceView}
      validateFormFields={validateFormFields}
      ref={formSectionRef}
    >
      <FormInputs
        ref={formInputsRef}
        email={email}
        password={password}
        onEmailChange={setEmail}
        onPasswordChange={setPassword}
        isEditor={isEditor}
        forceView={forceView}
        clearGlobalError={() => setError(undefined)}
        autoFocus={autoFocus}
      />
      <div className={classes.resetPasswordContainer}>
        <ResetPasswordLink
          isEditor={isEditor}
          isPreview={isPreview}
          forceView={forceView}
          promptAuthPage={promptAuthPage}
          allowPostAuthRedirect={allowPostAuthRedirect}
        />
      </div>
    </FormSection>
  );
};
